import { createContext, useCallback, useContext, useMemo, useRef, useSyncExternalStore } from 'react';

import type { AltSummFields, AltSummVariant } from 'contexts/summarian/summarian.utils';

type StoreKey = `${string}:${string}`;

const toKey = (contentId: string, variant: AltSummVariant): StoreKey => `${contentId}:${variant}`;

interface AltSummOverrideContextType {
  setOverride: (contentId: string, variant: AltSummVariant, fields: AltSummFields) => void;
  clearOverride: (contentId: string, variant: AltSummVariant) => void;
  clearAllOverridesForContent: (contentId: string) => void;
  subscribeToOverride: (contentId: string, variant: AltSummVariant, listener: () => void) => () => void;
  getOverride: (contentId: string, variant: AltSummVariant) => AltSummFields | undefined;
}

const AltSummOverrideContext = createContext<AltSummOverrideContextType>({
  setOverride: () => {},
  clearOverride: () => {},
  clearAllOverridesForContent: () => {},
  subscribeToOverride: () => () => {},
  getOverride: () => undefined
});

export const AltSummOverrideProvider = ({ children }: { children: React.ReactNode }) => {
  const dataRef = useRef<Map<StoreKey, AltSummFields>>(new Map());
  // Tracks how many Apply clicks are still awaiting an SNS confirmation.
  const pendingRef = useRef<Map<StoreKey, number>>(new Map());
  const listenersRef = useRef<Map<StoreKey, Set<() => void>>>(new Map());

  const notify = useCallback((key: StoreKey) => {
    listenersRef.current.get(key)?.forEach((l) => l());
  }, []);

  const setOverride = useCallback(
    (contentId: string, variant: AltSummVariant, fields: AltSummFields) => {
      const key = toKey(contentId, variant);
      dataRef.current.set(key, fields);
      pendingRef.current.set(key, (pendingRef.current.get(key) ?? 0) + 1);
      notify(key);
    },
    [notify]
  );

  // Called once per SNS event. Only removes the override when all pending Apply
  // confirmations have been received.
  const clearOverride = useCallback(
    (contentId: string, variant: AltSummVariant) => {
      const key = toKey(contentId, variant);
      const remaining = (pendingRef.current.get(key) ?? 1) - 1;
      if (remaining <= 0) {
        dataRef.current.delete(key);
        pendingRef.current.delete(key);
      } else {
        pendingRef.current.set(key, remaining);
      }
      notify(key);
    },
    [notify]
  );

  const clearAllOverridesForContent = useCallback(
    (contentId: string) => {
      [...dataRef.current.keys()]
        .filter((key) => key.startsWith(`${contentId}:`))
        .forEach((key) => {
          dataRef.current.delete(key);
          pendingRef.current.delete(key);
          notify(key);
        });
    },
    [notify]
  );

  const subscribeToOverride = useCallback((contentId: string, variant: AltSummVariant, listener: () => void) => {
    const key = toKey(contentId, variant);
    if (!listenersRef.current.has(key)) listenersRef.current.set(key, new Set());
    listenersRef.current.get(key)!.add(listener);
    return () => {
      const listeners = listenersRef.current.get(key);
      if (listeners) {
        listeners.delete(listener);
        if (listeners.size === 0) listenersRef.current.delete(key);
      }
    };
  }, []);

  const getOverride = useCallback(
    (contentId: string, variant: AltSummVariant) => dataRef.current.get(toKey(contentId, variant)),
    []
  );

  const value = useMemo(
    () => ({ setOverride, clearOverride, clearAllOverridesForContent, subscribeToOverride, getOverride }),
    [setOverride, clearOverride, clearAllOverridesForContent, subscribeToOverride, getOverride]
  );

  return <AltSummOverrideContext.Provider value={value}>{children}</AltSummOverrideContext.Provider>;
};

export const useAltSummOverrideContext = () => useContext(AltSummOverrideContext);

export const useAltSummOverride = (contentId: string, variant: AltSummVariant): AltSummFields | undefined => {
  const { subscribeToOverride, getOverride } = useAltSummOverrideContext();

  return useSyncExternalStore(
    useCallback(
      (listener) => subscribeToOverride(contentId, variant, listener),
      [subscribeToOverride, contentId, variant]
    ),
    useCallback(() => getOverride(contentId, variant), [getOverride, contentId, variant])
  );
};
